import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  timeout: 30000,
});

let _accessToken = null;

export function setAuthToken(token) {
  _accessToken = token;
  if (token) {
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    localStorage.setItem("eb_access_token", token);
  } else {
    delete api.defaults.headers.common["Authorization"];
    localStorage.removeItem("eb_access_token");
  }
}

export function getAuthToken() {
  if (_accessToken) return _accessToken;
  const stored = localStorage.getItem("eb_access_token");
  if (stored) {
    _accessToken = stored;
    api.defaults.headers.common["Authorization"] = `Bearer ${stored}`;
  }
  return _accessToken;
}

// v2 PR-2C.1-followup — Bulletproof axios-error stringifier.
//
// Why this is a HARD GUARANTEE rather than best-effort: every `toast.error(...)`
// in the app routes through here. If this ever returns "[object Object]" we
// strand the user with a UX worse than the underlying error itself.
//
// FastAPI surfaces three detail shapes in our stack:
//   1. `{detail: "human readable string"}`                              ← rare
//   2. `{detail: [{msg, type, loc}, ...]}`                              ← pydantic 422
//   3. `{detail: {error: "code", message: "human readable"}}`           ← our convention
//
// PRE-FIX BUG: shape 3 produced `String({error,message}) === "[object Object]"`
// because the recursive extractor only checked `.msg`. Fixed below by walking
// `.message → .msg → .detail → .error` and only falling back to JSON.stringify
// when none are strings. `String(obj)` is NEVER used so `[object Object]` is
// structurally impossible. Covered by `scripts/test-format-api-error.js`.
export function formatApiError(err) {
  const out = _formatApiErrorInner(err);
  // Last-line defence: if anything in the chain ever yields a value whose
  // `String()` starts with "[object" we surface a safe fallback instead of
  // showing the user the noisy "[object Object]". This is structurally
  // unreachable today (`_stringifyDetail` never calls `String(obj)`) but it
  // pins the contract: NO future regression of this class ever leaks to the
  // user. The console.error gives engineers a breadcrumb in production.
  if (typeof out === "string" && out.startsWith("[object")) {
    // eslint-disable-next-line no-console
    console.error("formatApiError: produced a non-string-ish value, falling back. Input:", err);
    return "An unexpected error occurred. Please try again.";
  }
  return typeof out === "string" ? out : "An unexpected error occurred. Please try again.";
}

function _formatApiErrorInner(err) {
  if (!err) return "Something went wrong";
  if (typeof err === "string") return err;
  // Axios shape: err.response.data.detail. Network errors lack .response.
  const detail = err.response?.data?.detail;
  if (detail == null) {
    if (typeof err.message === "string" && err.message) return err.message;
    if (err instanceof Error) return err.name || "Request failed";
    return _stringifyDetail(err);
  }
  return _stringifyDetail(detail);
}

function _stringifyDetail(detail) {
  if (detail == null) return "Request failed";
  if (typeof detail === "string") return detail;
  if (typeof detail === "number" || typeof detail === "boolean") return String(detail);
  if (Array.isArray(detail)) {
    const parts = detail.map(_stringifyDetail).filter(Boolean);
    return parts.length ? parts.join(" · ") : "Request failed";
  }
  if (typeof detail === "object") {
    // Field-priority order matches the conventions we ship from the backend.
    for (const field of ["message", "msg", "detail", "error"]) {
      const v = detail[field];
      if (typeof v === "string" && v.trim()) return v;
    }
    // No usable string field — fall back to JSON.stringify (informative)
    // rather than String(obj) (which produces "[object Object]").
    try { return JSON.stringify(detail); } catch { /* circular */ }
  }
  return "Request failed";
}

// Initialise from storage on module load
getAuthToken();

export default api;
